import { ClinicAuthError } from "@auth/clinicAuthClient";

/**
 * Per-mutation annotations read by the global write-failure handler in
 * {@link queryClient}. Typed onto every `useMutation` via the module
 * augmentation below, so `meta` is checked rather than a free-form object.
 */
// A type alias, not an interface: query-core resolves `mutationMeta` through a
// `TMutationMeta extends Record<string, unknown>` constraint, and an interface
// has no implicit index signature, so it silently fails the check and `meta`
// falls back to `Record<string, unknown>`.
export type MutationMeta = {
  /**
   * What the user was trying to do, as a verb phrase that completes
   * "Couldn't …" — e.g. `"approve the estimate"`. Omitted falls back to a
   * generic phrase, so a new mutation still reports something sensible.
   */
  action?: string;
  /**
   * Set when the call site renders its own failure UI, which keeps the global
   * notice quiet so the user isn't told twice.
   */
  handled?: boolean;
};

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: MutationMeta;
  }
}

const FALLBACK_ACTION = "complete that action";

/**
 * One sentence for a failed write, shared by the global notice and any inline
 * display, so the same failure is never worded two ways.
 *
 * Every branch leads with the reason and closes with what didn't happen, since
 * the reason is what decides whether the user retries, waits, or fetches
 * someone with the right role. `request()` funnels every transport and status
 * failure into {@link ClinicAuthError}, so anything else reaching here is an
 * unexpected throw and gets the generic line.
 */
export const describeMutationError = (
  error: unknown,
  action?: string,
): string => {
  const what = action ?? FALLBACK_ACTION;
  const generic = `Couldn’t ${what}. Try again.`;

  if (!(error instanceof ClinicAuthError)) return generic;

  if (error.code === "network_error") return `No connection. Couldn’t ${what}.`;
  if (error.code === "invalid_credentials")
    return "Your session expired. Sign in again.";
  // 429 arrives as `rate_limited` from the auth client but as a plain status
  // from `request()`, so both spellings resolve here.
  if (error.code === "rate_limited" || error.status === 429)
    return "Too many requests. Wait a moment, then try again.";

  switch (error.status) {
    case 403:
      return `You don’t have permission to ${what}.`;
    case 404:
      return `That’s no longer available. Couldn’t ${what}.`;
    case 409:
      return `Someone else got there first. Couldn’t ${what}.`;
    default:
      if (error.status !== undefined && error.status >= 500)
        return `Something went wrong on our end. Couldn’t ${what}.`;
      return generic;
  }
};
